import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getMonths } from '@/services/monthlyPerformance'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import type { MonthlyPerformanceMonthSummary } from '@/types/monthlyPerformance'
import { defaultReportMonth } from './monthlyPerformanceUtils'

export function useMonthlyPerformanceMonth() {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlMonth = searchParams.get('month')

  const [months, setMonths] = useState<MonthlyPerformanceMonthSummary[]>([])
  const [month, setMonth] = useState<string | null>(() => searchParams.get('month'))

  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams
  const setSearchParamsRef = useRef(setSearchParams)
  setSearchParamsRef.current = setSearchParams

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const monthsData = await getMonths()
        if (cancelled) return
        setMonths(monthsData)
        setMonth((current) => {
          if (current !== null) return current
          const fromUrl = searchParamsRef.current.get('month')
          const initial = fromUrl ?? defaultReportMonth(monthsData)
          return initial ?? current
        })
      } catch (error) {
        if (!cancelled) {
          toast.error(formatApiError(error, 'Failed to load months'))
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!urlMonth) return
    setMonth((current) => (current === urlMonth ? current : urlMonth))
  }, [urlMonth])

  useEffect(() => {
    if (!month) return
    const currentUrlMonth = searchParamsRef.current.get('month')
    if (currentUrlMonth === month) return
    const next = new URLSearchParams(searchParamsRef.current)
    next.set('month', month)
    setSearchParamsRef.current(next, { replace: true })
  }, [month])

  return { months, setMonths, month, setMonth }
}
